import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import type { CaseDetail } from '../api/types'
import { ClassifierPanel } from '../components/ClassifierPanel'
import { CorrectnessBadge } from '../components/CorrectnessBadge'
import { MeshViewer } from '../components/MeshViewer'
import { useViewMode } from '../components/ModeProvider'
import { SliceViewer } from '../components/SliceViewer'
import { Term } from '../components/Term'
import { useCaseNumber } from '../lib/case-index'
import { buildPredictionView, correctness } from '../lib/predict'

const ROLE_LABEL: Record<string, string> = {
  clean_malignant_ambl: 'Belirgin kötü huylu lezyon (AMBL)',
  clean_benign_ambl: 'Belirgin iyi huylu lezyon (AMBL)',
  borderline_ambl_malignant: 'Sınır vaka, model kararsız',
  multifocal_ambl: 'Çok odaklı lezyon (AMBL)',
  bd_malignant_domain_shift: 'Farklı kaynak, kötü huylu (BD)',
  bd_benign: 'Farklı kaynak, iyi huylu (BD)',
  second_benign_ambl: 'İyi huylu lezyon (AMBL)',
  second_malignant_ambl: 'Kötü huylu lezyon (AMBL)',
  user_upload: 'Kullanıcı yüklemesi',
}

function sizeHint(volumeMl: number | null | undefined): string | null {
  if (volumeMl == null || !Number.isFinite(volumeMl) || volumeMl <= 0) return null
  if (volumeMl < 0.1) return 'buğday tanesi büyüklüğünde'
  if (volumeMl < 0.5) return 'mercimek tanesi büyüklüğünde'
  if (volumeMl < 2) return 'fındık büyüklüğünde'
  if (volumeMl < 8) return 'ceviz büyüklüğünde'
  if (volumeMl < 25) return 'erik büyüklüğünde'
  if (volumeMl < 65) return 'küçük yumurta büyüklüğünde'
  if (volumeMl < 150) return 'tenis topu büyüklüğünde'
  return 'elma büyüklüğünde'
}

export function CaseViewer() {
  const { caseId } = useParams<{ caseId: string }>()
  const { isAdvanced, isSimple } = useViewMode()
  const [state, setState] = useState<{
    caseId: string | null
    detail: CaseDetail | null
    error: string | null
  }>({ caseId: null, detail: null, error: null })
  const [searchParams, setSearchParams] = useSearchParams()
  const initialDoctor = searchParams.get('doctor') === '1'
  const [doctorMode, setDoctorMode] = useState(initialDoctor)
  const [revealed, setRevealed] = useState(false)
  const number = useCaseNumber(caseId ?? null)

  useEffect(() => {
    if (!caseId) return
    let live = true
    setDoctorMode(searchParams.get('doctor') === '1')
    setRevealed(false)
    api.getCase(caseId)
      .then((detail) => {
        if (live) setState({ caseId, detail, error: null })
      })
      .catch((e) => {
        if (live) setState({ caseId, detail: null, error: String(e) })
      })
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId])

  const active = state.caseId === caseId ? state : { detail: null, error: null }
  const { detail, error } = active

  if (error)
    return (
      <div className="text-malignant text-sm">
        {error}
        <div className="mt-2">
          <Link to="/cases" className="text-accent">← Vakalara dön</Link>
        </div>
      </div>
    )
  if (!detail) return <p className="text-text-dim">Yükleniyor…</p>

  const meshUrl = detail.artifacts['mesh.glb']
  const imageUrl = detail.artifacts['image.nii.gz']
  const preImageUrl = detail.artifacts['image_pre.nii.gz'] ?? null
  const maskUrl = detail.artifacts['mask.nii.gz']
  const predictedMaskUrl =
    detail.mask_kind === 'dual' ? detail.artifacts['mask_predicted.nii.gz'] : null
  const meshEmpty = !!detail.mesh.skipped || (detail.mesh.n_faces ?? 0) === 0
  const role = detail.demo_role ? ROLE_LABEL[detail.demo_role] || detail.demo_role : null
  const view = buildPredictionView(detail)
  const correct = correctness(detail, view)
  const isMalignant = detail.label === 'Malignant'
  const labelText = isMalignant ? 'Kötü huylu' : detail.label === 'Benign' ? 'İyi huylu' : null
  const labelCls = isMalignant ? 'text-malignant' : 'text-benign'

  const heading = number ? `Vaka #${number}` : detail.id
  const showAnswers = !doctorMode || revealed
  // Panels in the grid: 3D model (normal mode only) + axial slice (always) +
  // classifier (only once answers are shown).
  const panelCount = (doctorMode ? 0 : 1) + 1 + (showAnswers ? 1 : 0)
  const gridColsCls =
    panelCount >= 3 ? 'lg:grid-cols-3' : panelCount === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-1'

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 mb-4">
        <div className="min-w-0">
          <Link to="/cases" className="text-accent text-sm">← Tüm vakalar</Link>
          <h1 className="text-xl sm:text-2xl font-semibold text-text mt-1 break-all">
            {heading}
            {isAdvanced && number != null && (
              <span className="ml-2 font-normal text-sm text-text-dim">
                {detail.id}
              </span>
            )}
          </h1>
          {isAdvanced && (
            <p className="text-text-dim text-sm">
              {detail.cohort} <Term id="kohort">kohortu</Term>, hasta{' '}
              {detail.patient_id}, lezyon #{detail.segment_number}
              {showAnswers && role && (
                <span className="ml-2 inline-block text-xs px-2 py-0.5 bg-surface-2 rounded text-text-dim">
                  {role}
                </span>
              )}
            </p>
          )}
          {showAnswers && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {labelText && (
                <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded border ${labelCls} border-current/30`}>
                  Gerçek etiket: {labelText}
                </span>
              )}
              {correct !== 'unknown' && (
                <CorrectnessBadge correctness={correct} size="md" />
              )}
            </div>
          )}
          {!showAnswers && (
            <p className="text-text-dim text-xs mt-2 italic max-w-md">
              Doktor modu açık. Gerçek etiket ve modelin tahmini siz hazır olana
              kadar gizlendi.
            </p>
          )}
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2">
          <button
            type="button"
            onClick={() => {
              const next = !doctorMode
              setDoctorMode(next)
              setRevealed(false)
              setSearchParams(
                (prev) => {
                  const p = new URLSearchParams(prev)
                  if (next) p.set('doctor', '1')
                  else p.delete('doctor')
                  return p
                },
                { replace: true },
              )
            }}
            className="text-xs px-3 py-1.5 rounded border border-border bg-surface-2 text-text hover:bg-surface"
          >
            {doctorMode ? 'Standart görünüme dön' : 'Doktor gibi izle'}
          </button>
          {showAnswers && (
            <div className="flex items-baseline gap-2 sm:flex-col sm:items-end sm:gap-0">
              <div className="text-xs text-text-dim">Lezyon hacmi</div>
              <div className="font-mono text-text">
                {detail.mesh.lesion_volume_ml?.toFixed(2)} mL
              </div>
              {sizeHint(detail.mesh.lesion_volume_ml) && (
                <div className="text-[11px] text-text-dim italic">
                  ≈ {sizeHint(detail.mesh.lesion_volume_ml)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {doctorMode && !revealed && (
        <DoctorIntro />
      )}

      <div className={`grid grid-cols-1 gap-4 ${gridColsCls}`}>
        {!doctorMode && (
          <div>
            <div className="text-xs text-text-dim mb-1.5">3 boyutlu lezyon ve göğüs modeli</div>
            {meshUrl ? (
              <MeshViewer
                src={meshUrl}
                empty={meshEmpty}
                lesionDimsMm={
                  detail?.mesh?.lesion_dims_mm && detail.mesh.lesion_dims_mm.length === 3
                    ? (detail.mesh.lesion_dims_mm as [number, number, number])
                    : null
                }
              />
            ) : (
              <div className="aspect-square bg-surface rounded text-text-dim text-sm flex items-center justify-center">
                3 boyutlu model bulunamadı.
              </div>
            )}
          </div>
        )}
        <div>
          <div className="text-xs text-text-dim mb-1.5">
            Aksiyel kesit
            {showAnswers && detail.mask_kind === 'dual' && ' (yeşil: uzman, kırmızı: model, sarı: örtüşme)'}
            {showAnswers && detail.mask_kind === 'predicted' && ' (kırmızı: modelin tahmini)'}
            {showAnswers && (detail.mask_kind === 'expert' || !detail.mask_kind) && ' (lezyon kırmızıyla işaretli)'}
            {!showAnswers && ', kesitleri tarayıp lezyonu kendiniz bulmayı deneyin'}
          </div>
          {imageUrl && maskUrl ? (
            <SliceViewer
              imageUrl={imageUrl}
              preImageUrl={preImageUrl}
              maskUrl={maskUrl}
              predictedMaskUrl={!showAnswers ? null : predictedMaskUrl}
              discoveryMode={!showAnswers}
            />
          ) : (
            <div className="aspect-square bg-surface rounded text-text-dim text-sm flex items-center justify-center">
              Görüntü bulunamadı.
            </div>
          )}
          {showAnswers && (
            <SegmentationNote detail={detail} isAdvanced={isAdvanced} />
          )}
        </div>
        {showAnswers && (
          <div>
            <ClassifierPanel detail={detail} />
          </div>
        )}
      </div>

      {doctorMode && !revealed && (
        <div className="mt-6 border-t border-border pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-text-dim text-sm leading-relaxed max-w-2xl">
            Lezyonu kendi gözünüzle bulup iyi mi yoksa kötü huylu mu olduğuna
            karar verdiğinizde, gerçek etiketi ve modelin tahminini görmek için
            aşağıdaki düğmeye basın.
          </p>
          <button
            type="button"
            onClick={() => setRevealed(true)}
            className="px-4 py-2 rounded bg-accent text-accent-fg font-medium text-sm hover:opacity-90 self-start sm:self-auto"
          >
            Cevabı göster
          </button>
        </div>
      )}
      {isSimple && showAnswers && (
        <p className="text-text-dim text-[11px] mt-4 leading-relaxed max-w-3xl">
          Görünümü değiştirmek için sağ üstteki <span className="text-text font-medium">Basit / İleri</span> düğmesini kullanabilirsiniz.
        </p>
      )}
    </div>
  )
}

function SegmentationNote({
  detail,
  isAdvanced,
}: {
  detail: CaseDetail
  isAdvanced: boolean
}) {
  const kind = detail.mask_kind
  if (kind === 'dual') {
    const dice = detail.segmentation_dice
    if (!dice) return null
    const pct = Math.round(dice.dice_matched * 100)
    return (
      <div className="mt-2 text-[11px] sm:text-xs text-text-dim border border-border bg-surface rounded p-2.5 leading-relaxed">
        <div className="text-text font-medium text-sm">
          Segmentasyon başarısı: <span className="text-accent">%{pct}</span>
        </div>
        <div>
          Modelin tahmini ile uzman anotasyonu arasındaki örtüşme (Dice). %100 tam uyum, %0 hiç örtüşme yok demektir.
        </div>
        {dice.n_pred_components > 1 && (
          <div className="mt-1">
            Model bu çekimde {dice.n_pred_components} ayrı bölge işaretledi. Yukarıdaki oran, uzman lezyonuyla en çok örtüşen bölge esas alınarak hesaplanmıştır.
          </div>
        )}
        {isAdvanced && (
          <div className="mt-1 font-mono text-[10px] sm:text-[11px]">
            ham Dice: {dice.dice_raw.toFixed(3)} · eşleştirilmiş Dice: {dice.dice_matched.toFixed(3)} · uzman: {dice.expert_voxels} vx · eşleşen: {dice.pred_voxels_matched} vx · toplam tahmin: {dice.pred_voxels_total} vx
          </div>
        )}
      </div>
    )
  }
  if (kind === 'predicted') {
    return (
      <div className="mt-2 text-[11px] text-text-dim italic">
        Bu kohort için uzman anotasyonu paylaşılmadığından segmentasyon başarı oranı verilemiyor. Gösterilen kırmızı bölge modelin tahminidir.
      </div>
    )
  }
  if (kind === 'expert') {
    return (
      <div className="mt-2 text-[11px] text-text-dim italic">
        Bu vaka için modelin segmentasyon çıktısı bu sürümde mevcut değil. Gösterilen kırmızı bölge uzman anotasyonudur.
      </div>
    )
  }
  return null
}

function DoctorIntro() {
  return (
    <div className="mb-4 border border-warn/30 bg-warn/5 rounded p-3 sm:p-4">
      <div className="text-warn text-sm font-medium mb-1">Doktor modu</div>
      <p className="text-text-dim text-xs sm:text-sm leading-relaxed">
        Aksiyel kesitler ilk dilimden başlar, kırmızı maske kapalıdır. Bir
        radyologun başka bir bilgiye sahip olmadan vakaya bakarken yaptığı gibi,
        kesitleri tarayıp lezyonu kendiniz bulmayı deneyin. 3 boyutlu model
        görünür durumdadır, ancak iyi huylu mu kötü huylu mu olduğu ve modelin
        sınıflandırma tahmini gizlenmiştir. Hazır olduğunuzda sayfanın altındaki{' '}
        <span className="text-text">Cevabı göster</span> düğmesine basarak
        karşılaştırma yapabilirsiniz.
      </p>
    </div>
  )
}
